import React from 'react';

import type { VideoMediaStrategy } from '../VideoRecorder';
import type { VoiceNoteStep, VoiceScriptRequest } from './VoiceNoteScriptPicker';
import Button from '../Input2/Button';
import Textarea from '../Input2/Textarea';
import { StepIconSvg } from '../StepIcon';
import { Arrows, General, Images } from '../StrokeIcons';
import { getTextFromHtml } from '../utils/htmlUtils';
import { VideoRecorder } from '../VideoRecorder';
import { VoiceNoteLeadStep, VoiceNoteStepper, VoiceNoteTakesStep } from './VoiceNoteScriptPicker';

export type VideoNoteActionCardPureProps = {
  /** Write up to three takes tuned for an on-camera teleprompter read. Absent → the rep types the script. */
  onGenerateVideoScripts?: (request: VoiceScriptRequest) => Promise<string[]>;
  /** Dossier talking points, best first — offered as "lead with" chips. */
  angles?: string[];
  /** Upload + send the take. Resolve to close the card; reject to keep the take for a retry. */
  onSendVideo: (video: Blob, durationMs: number, script: string) => Promise<void>;
  /** Camera strategy for surfaces where `getUserMedia` needs help (the extension). */
  mediaStrategy?: VideoMediaStrategy;
};

/**
 * "Record a video note": the video sibling of the voice-note card. Same script flow (lead with →
 * pick a take → edit), then the rep reads it on camera off a teleprompter and sends the take as a
 * LinkedIn video message. No AI avatar or voice clone — it is the rep, recorded.
 */
export function VideoNoteActionCardPure({
  onGenerateVideoScripts,
  angles = [],
  onSendVideo,
  mediaStrategy,
}: VideoNoteActionCardPureProps) {
  const canWriteScripts = onGenerateVideoScripts != null;
  const firstStep: VoiceNoteStep = canWriteScripts ? 'lead' : 'deliver';

  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<VoiceNoteStep>(firstStep);
  const [recording, setRecording] = React.useState(false);
  const [scriptRaw, setScriptRaw] = React.useState('');
  const [takes, setTakes] = React.useState<string[]>([]);
  const [isWriting, setIsWriting] = React.useState(false);
  const [activeAngle, setActiveAngle] = React.useState<string | null>(null);
  const [lastRequest, setLastRequest] = React.useState<VoiceScriptRequest | null>(null);
  const [scriptError, setScriptError] = React.useState<string | null>(null);

  const script = scriptRaw;
  // Same defence as the voice card: anything HTML-shaped is flattened to plain spoken text.
  const setScript = (value: string) =>
    setScriptRaw(/<\w+[^>]*>|&\w+;/.test(value) ? getTextFromHtml(value, true) : value);

  const selectedTake = React.useMemo(() => {
    const index = takes.indexOf(script);
    return index === -1 ? null : index;
  }, [takes, script]);

  const writeScripts = async (request: VoiceScriptRequest) => {
    if (!onGenerateVideoScripts) return;
    setIsWriting(true);
    setScriptError(null);
    setActiveAngle(request.angle ?? null);
    setLastRequest(request);
    try {
      const next = (await onGenerateVideoScripts(request)).map((t) => t.trim()).filter(Boolean);
      setTakes(next);
      if (next.length === 0) setScriptError('Nothing came back. Try another angle.');
    } catch {
      setScriptError("Couldn't write a script. Try again.");
    } finally {
      setIsWriting(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setStep(firstStep);
    setRecording(false);
    setScriptRaw('');
    setTakes([]);
    setActiveAngle(null);
    setLastRequest(null);
    setScriptError(null);
  };

  if (!open) {
    return (
      <Button
        className="w-full"
        color="blue"
        tone="soft"
        onClick={() => setOpen(true)}
        icon={<StepIconSvg iconType="video_note" />}
        data-testid="video-note-open">
        <span>
          Record a{' '}
          <span className="font-medium text-blue-700 underline decoration-blue-300/50 underline-offset-2 group-hover/btn:text-white group-hover/btn:decoration-white/60 dark:text-blue-300 dark:decoration-blue-500/50">
            Video Note
          </span>
        </span>
      </Button>
    );
  }

  const previousStep: VoiceNoteStep | null = recording
    ? null
    : step === 'deliver' && canWriteScripts
      ? takes.length > 0 || isWriting
        ? 'script'
        : 'lead'
      : step === 'script'
        ? 'lead'
        : null;
  const reachable: VoiceNoteStep[] = canWriteScripts
    ? ([
        'lead',
        ...(takes.length > 0 ? (['script'] as const) : []),
        ...(script.trim() ? (['deliver'] as const) : []),
      ] as VoiceNoteStep[])
    : [];
  const title =
    step === 'lead'
      ? 'What do you want to lead with?'
      : step === 'script'
        ? 'Pick a take'
        : recording
          ? 'Read it to the camera'
          : 'Your script';

  return (
    <div
      className="flex flex-col overflow-hidden rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950"
      data-testid="video-note-card">
      <div className="flex items-center gap-2 px-3 py-2">
        {previousStep != null ? (
          <Button
            size="sm"
            text
            color="gray"
            tooltip="Back"
            onClick={() => setStep(previousStep)}
            icon={<Arrows.ChevronLeft className="size-4" />}
          />
        ) : (
          <Images.Camera01 className="size-5 shrink-0 text-red-600" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-astronaut-700 dark:text-astronaut-300">
          {title}
        </span>
        {canWriteScripts && !recording && <VoiceNoteStepper step={step} reachable={reachable} onStepClick={setStep} />}
        <Button
          size="sm"
          text
          color="gray"
          tooltip="Cancel"
          onClick={reset}
          data-testid="video-note-cancel"
          icon={<General.XMark className="size-4" />}
        />
      </div>

      {step === 'lead' && (
        <VoiceNoteLeadStep
          angles={angles}
          activeAngle={activeAngle}
          disabled={isWriting}
          onGenerate={(request) => {
            setStep('script');
            void writeScripts(request);
          }}
        />
      )}

      {step === 'script' && (
        <VoiceNoteTakesStep
          takes={takes}
          selectedTake={selectedTake}
          isGenerating={isWriting}
          error={scriptError}
          onPickTake={(index) => {
            const take = takes[index];
            if (take == null) return;
            setScript(take);
            setStep('deliver');
          }}
          onRegenerate={lastRequest ? () => void writeScripts(lastRequest) : undefined}
        />
      )}

      {step === 'deliver' && !recording && (
        <div className="flex flex-col gap-2 pb-3">
          <Textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder={canWriteScripts ? 'Type what to say, or go back and let it write…' : 'Type what to say…'}
            rows={5}
            bordered={false}
            color="red"
            tone="outline"
            shape="square"
            className="w-full"
            data-testid="video-note-script"
          />
          <div className="px-3">
            <Button
              size="sm"
              color="red"
              className="w-full"
              disabled={!script.trim()}
              tooltip={script.trim() ? 'Open the camera with this script on the teleprompter' : 'Write a script first'}
              onClick={() => setRecording(true)}
              data-testid="video-note-record"
              icon={<Images.Camera01 />}>
              Record with teleprompter
            </Button>
          </div>
        </div>
      )}

      {step === 'deliver' && recording && (
        <div className="px-3 pb-3">
          <VideoRecorder
            script={script}
            autoOpen
            mediaStrategy={mediaStrategy}
            onCancel={() => setRecording(false)}
            onSend={async (video, durationMs) => {
              await onSendVideo(video, durationMs, script);
              reset();
            }}
          />
        </div>
      )}
    </div>
  );
}
